import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconUserPlus, IconEdit, IconTrash, IconUpload } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';
import ImportDialog from '../../../../../ui-component/extended/ImportDialog';
import PaginationBar from '../../../../../ui-component/extended/PaginationBar';
import LoadingBox from '../../../../../ui-component/extended/LoadingBox';
import useRequestGuard from '../../../../../hooks/useRequestGuard';

const STATUS_LABEL = {
    active: { label: 'Ativo', color: 'success' },
    on_leave: { label: 'Afastado', color: 'warning' },
    terminated: { label: 'Desligado', color: 'default' }
};

const ViewAllColaboradores = () => {
    const history = useHistory();
    const { start, isCurrent } = useRequestGuard();
    const [appId, setAppId] = useState(null);
    const [colaboradores, setColaboradores] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ count: 0, numPages: 1 });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadDepartments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        loadColaboradores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, statusFilter, departmentFilter, page]);

    const loadDepartments = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/departments/`);
            setDepartments(res.data.departments || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadColaboradores = async () => {
        const requestId = start();
        setLoading(true);
        try {
            const params = { page };
            if (statusFilter) params.status = statusFilter;
            if (departmentFilter) params.department = departmentFilter;
            const res = await api.get(`v1/apps/${appId}/colaboradores/`, { params });
            if (!isCurrent(requestId)) return;
            setColaboradores(res.data.colaboradores || []);
            setPagination({ count: res.data.count || 0, numPages: res.data.num_pages || 1 });
        } catch (error) {
            if (!isCurrent(requestId)) return;
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar colaboradores' });
        } finally {
            if (isCurrent(requestId)) setLoading(false);
        }
    };

    const handleDelete = async (colaborador) => {
        if (!window.confirm(`Excluir o colaborador "${colaborador.full_name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/colaboradores/${colaborador.id}/`);
            loadColaboradores();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir colaborador' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconUserPlus}
                gradient="blue"
                title="Colaboradores"
                subtitle="Cadastro de funcionários"
                actions={
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<IconUserPlus size={18} />} onClick={() => history.push('/dashboard/hr/colaboradores/new')}>
                            Novo Colaborador
                        </Button>
                        <Button variant="outlined" startIcon={<IconUpload size={18} />} onClick={() => setImportDialogOpen(true)}>
                            Importar
                        </Button>
                    </Stack>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                    select
                    label="Status"
                    size="small"
                    sx={{ minWidth: 180 }}
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                    }}
                >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="active">Ativo</MenuItem>
                    <MenuItem value="on_leave">Afastado</MenuItem>
                    <MenuItem value="terminated">Desligado</MenuItem>
                </TextField>
                <TextField
                    select
                    label="Departamento"
                    size="small"
                    sx={{ minWidth: 220 }}
                    value={departmentFilter}
                    onChange={(e) => {
                        setDepartmentFilter(e.target.value);
                        setPage(1);
                    }}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {departments.map((department) => (
                        <MenuItem key={department.id} value={department.id}>
                            {department.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            <GlassCard>
                <CardContent>
                    {loading ? (
                        <LoadingBox label="Carregando colaboradores..." />
                    ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Cargo</TableCell>
                                <TableCell>Departamento</TableCell>
                                <TableCell>Admissão</TableCell>
                                <TableCell>Salário</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {colaboradores.map((colaborador) => {
                                const statusInfo = STATUS_LABEL[colaborador.status] || { label: colaborador.status, color: 'default' };
                                return (
                                    <TableRow key={colaborador.id} hover>
                                        <TableCell>{colaborador.full_name}</TableCell>
                                        <TableCell>{colaborador.role_name || '—'}</TableCell>
                                        <TableCell>{colaborador.department_name || '—'}</TableCell>
                                        <TableCell>{colaborador.admission_date}</TableCell>
                                        <TableCell>R$ {colaborador.salary}</TableCell>
                                        <TableCell>
                                            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={() => history.push(`/dashboard/hr/colaboradores/${colaborador.id}/edit`)}>
                                                <IconEdit size={16} />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleDelete(colaborador)}>
                                                <IconTrash size={16} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {colaboradores.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Nenhum colaborador cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    )}
                </CardContent>
            </GlassCard>

            <PaginationBar page={page} numPages={pagination.numPages} count={pagination.count} onChange={setPage} />

            <ImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                title="Importar Colaboradores"
                entityLabel="colaboradores"
                systemFields={[
                    { key: 'nome_completo', label: 'Nome Completo', required: true },
                    { key: 'data_admissao', label: 'Data de Admissão (AAAA-MM-DD)', required: true },
                    { key: 'salario', label: 'Salário', required: true },
                    { key: 'cargo', label: 'Cargo (precisa já existir)', required: false },
                    { key: 'departamento', label: 'Departamento (precisa já existir)', required: false },
                    { key: 'cpf', label: 'CPF', required: false },
                    { key: 'data_nascimento', label: 'Data de Nascimento (AAAA-MM-DD)', required: false },
                    { key: 'email', label: 'Email', required: false },
                    { key: 'telefone', label: 'Telefone', required: false },
                    { key: 'endereco', label: 'Endereço', required: false }
                ]}
                fieldsEndpoint={`v1/apps/${appId}/colaborador_fields/`}
                importUrl={`v1/apps/${appId}/colaboradores/import/`}
                onImported={loadColaboradores}
            />
        </Box>
    );
};

export default ViewAllColaboradores;
